/**
 * Aceptar una invitación al equipo — `OD-0060` D-04, el enlace del correo E-18
 * (`/invitacion#token=…`).
 *
 * El token llega en el fragmento y se borra de la barra al cargar
 * (`enlaces/enlace-de-correo.ts`). Aquí se manda a la API una vez, y lo demás
 * lo decide ella: a qué empresa es, si quien tiene la sesión es la persona
 * invitada y si queda sitio.
 *
 * ```text
 * 200  dentro: la API ha dejado la sesion en esa empresa  -> se carga la sesion
 * 401  sin sesion       -> entrar (o crear la cuenta) con ESE correo, y volver
 * 403  otro correo, o sin verificar -> el motivo, y salir para entrar con otra
 * 404 / 409  ya no vale, caducada, sin sitio... -> el motivo que da la API
 * ```
 *
 * Tras entrar, la aplicación vuelve aquí con el mismo token (`App.tsx`). Quien
 * todavía no tiene cuenta la crea, confirma el correo en otra pestaña y vuelve
 * a abrir el enlace: el token no se guarda en el dispositivo (`ADR-0004`).
 */
import { useEffect, useRef, useState } from "react";
import { api, apiEquipo } from "../api/cliente";
import { t } from "../i18n/es";
import { Aviso, Boton, Tarjeta } from "../ui/primitivas";

type Estado =
  | { readonly tipo: "aceptando" }
  | { readonly tipo: "sin-sesion" }
  | { readonly tipo: "otra-cuenta"; readonly motivo: string }
  | { readonly tipo: "sin-red" }
  | { readonly tipo: "no-vale"; readonly motivo: string };

export function PantallaInvitacion({
  token,
  hayConexion,
  onAceptada,
  onEntrar,
  onRegistro,
  onContinuar,
}: {
  token: string | null;
  hayConexion: boolean;
  onAceptada: () => void;
  onEntrar: () => void;
  onRegistro: () => void;
  onContinuar: () => void;
}) {
  const [estado, setEstado] = useState<Estado>({ tipo: "aceptando" });
  const [saliendo, setSaliendo] = useState(false);
  // React puede montar dos veces en desarrollo: la API es idempotente, pero no
  // hace falta pedirlo dos veces.
  const pedido = useRef(false);

  async function aceptar(tok: string): Promise<void> {
    setEstado({ tipo: "aceptando" });
    const r = await apiEquipo.aceptar(tok);
    if (r.ok) {
      onAceptada();
      return;
    }
    const motivo = r.error?.message ?? t.comun.errorGenerico;
    if (r.estado === 0) setEstado({ tipo: "sin-red" });
    else if (r.estado === 401) setEstado({ tipo: "sin-sesion" });
    else if (r.estado === 403) setEstado({ tipo: "otra-cuenta", motivo });
    else if (r.estado === 429) setEstado({ tipo: "no-vale", motivo: t.comun.demasiadosIntentos });
    else setEstado({ tipo: "no-vale", motivo });
  }

  useEffect(() => {
    if (token === null || pedido.current) return;
    pedido.current = true;
    void aceptar(token);
    // `aceptar` cambia en cada render; el token es lo único que cuenta.
  }, [token]);

  async function salirYEntrar(): Promise<void> {
    setSaliendo(true);
    await api.logout();
    setSaliendo(false);
    onEntrar();
  }

  if (token === null) {
    return (
      <Tarjeta titulo={t.invitacion.titulo}>
        <Aviso tono="error">{t.invitacion.rota}</Aviso>
        <Boton variante="secundario" onClick={onContinuar}>
          {t.invitacion.continuar}
        </Boton>
      </Tarjeta>
    );
  }

  return (
    <Tarjeta titulo={t.invitacion.titulo}>
      {estado.tipo === "aceptando" ? <p>{t.invitacion.aceptando}</p> : null}

      {estado.tipo === "sin-sesion" ? (
        <>
          <p>{t.invitacion.entrarPrimero}</p>
          <p className="ayuda-neutra">{t.invitacion.sinCuenta}</p>
          <div className="acciones-en-linea">
            <Boton onClick={onEntrar}>{t.invitacion.entrar}</Boton>
            <Boton variante="secundario" onClick={onRegistro}>
              {t.invitacion.crearCuenta}
            </Boton>
          </div>
        </>
      ) : null}

      {estado.tipo === "otra-cuenta" ? (
        <>
          <Aviso tono="aviso">{estado.motivo}</Aviso>
          <Boton variante="secundario" onClick={() => void salirYEntrar()} deshabilitado={saliendo}>
            {saliendo ? t.invitacion.saliendo : t.invitacion.otraCuenta}
          </Boton>
        </>
      ) : null}

      {estado.tipo === "sin-red" ? (
        <>
          <Aviso tono="aviso">{t.comun.sinRed}</Aviso>
          <Boton variante="secundario" onClick={() => void aceptar(token)} deshabilitado={!hayConexion}>
            {t.invitacion.reintentar}
          </Boton>
        </>
      ) : null}

      {estado.tipo === "no-vale" ? (
        <>
          <Aviso tono="error">{estado.motivo}</Aviso>
          <Boton variante="secundario" onClick={onContinuar}>
            {t.invitacion.continuar}
          </Boton>
        </>
      ) : null}
    </Tarjeta>
  );
}
